import React from 'react';
import { Col, Row } from 'react-bootstrap';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useGet360AnalysisQuery } from '../data-access/project.api';
import BiasLLMPlotsSection from '../../Bias/BiasLLMPlotsSection';
import { FairnessCard } from '../../Dashboard/Fairness/FairnessCard';
import ExplainabilityCard from '../../Dashboard/Explainability-ML/ExplainabilityCard';
import { AISafetyDetection } from '../../AISafety/AISafetyDetection';
import { PromptInjectionCard } from '../../Dashboard/PromptInjection/PromptInjectionCard';
import { PromptInjectionLLMCard } from '../../Dashboard/PromptInjection/PromptInjectionLLMCard';
import { PromptInjectionMLCard } from '../../Dashboard/PromptInjection/PromptInjectionMLCard';

interface Review360TabProps {
  projectId?: string;
}

const Review360Tab: React.FC<Review360TabProps> = ({ projectId }) => {
  // Fetch 360-aggregated data whenever this tab is rendered (keepUnusedDataFor: 0)
  const {
    data: analysis360,
    isLoading,
    isError,
    error,
    refetch
  } = useGet360AnalysisQuery(projectId!, {
    skip: !projectId
  });

  const detailedData = analysis360?.aggregated_results?.detailed_360_data;

  return (
    <Box sx={{ padding: '24px 32px' }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Compliance IQ Analysis Overview
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
        Comprehensive view of all analysis metrics aggregated by the latest Analysis
      </Typography>

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Fetching aggregated Compliance IQ analysis…
          </Typography>
        </Box>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Typography
              variant="body2"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => refetch()}
            >
              Retry
            </Typography>
          }
        >
          Could not load Compliance IQ aggregated data.{' '}
          {(error as any)?.data?.error || 'Showing per-tab data as fallback below.'}
        </Alert>
      )}

      {/* Bias Detection */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          Bias Detection
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {/* BiasLLMPlotsSection drives from Redux (bias has its own complex charting) */}
        <BiasLLMPlotsSection customSummary={detailedData?.bias} />
      </Box>

      {/* Fairness Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          Fairness Analysis
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <FairnessCard customData={detailedData?.fairness} />
      </Box>

      {/* Explainability Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          Explainability Analysis
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <ExplainabilityCard customData={detailedData?.explainability} />
      </Box>

      {/* AI Safety Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          AI Safety Detection
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <AISafetyDetection customData={detailedData?.ai_safety || detailedData?.abuse_ai} />
      </Box>

      {/* Prompt Injection Detection Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          Prompt Injection Detection
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Row className="g-4">
          <Col xs={12}>
            {/* PromptInjectionCard reads from validate-project (prompt_validation) — no aggregation needed */}
            <PromptInjectionCard />
          </Col>
          <Col xs={12} md={6}>
            <PromptInjectionLLMCard customAnalysis={detailedData?.prompt_injection?.LLM_PROMPT_INJECTION_ANALYSIS} />
          </Col>
          <Col xs={12} md={6}>
            <PromptInjectionMLCard customAnalysis={detailedData?.prompt_injection?.ML_PROMPT_INJECTION_ANALYSIS} />
          </Col>
        </Row>
      </Box>
    </Box>
  );
};

export default Review360Tab;
